import type { RestRequest, RestContext, ResponseFunction } from 'msw'
import type { IEntityDataItem, TEntityId } from '~/types/models'

/**
 * The API's "supplier product and supplier product item cannot both be set"
 * rule, for the mock's msService writes.
 *
 * The real server enforces it in the `validate_*service` trigger every
 * msService table carries (cynque-api-server, `ms_service.py`): a row may
 * name the supplier-product it was built from (a kit header) or the
 * supplier-product ITEM it was built from (a kit item), never both. A write
 * that does is refused with a 409.
 *
 * WHY THE MOCK NEEDS IT: a nested Kit's header carries its own
 * `supplierProduct`, and the obvious way to pair it with the item that produced
 * it is to stamp that `supplierProductItem` on it too. The mock stored that
 * happily, so every unit and e2e test would have passed while every save
 * failed against the real API. Same lesson as `versionConflict.ts` and
 * `validationFailure.ts`: before concluding "this can't happen", check that the
 * mock is capable of making it happen.
 */

function _isSet(value: unknown): boolean {
  return value !== undefined && value !== null
}

/**
 * The first submitted row that would end up with both set, if any.
 *
 * A CREATE row is judged on its own. An UPDATE is a merge patch, so a key the
 * patch omits keeps its stored value -- pass `listData` and a patch that adds
 * a `supplierProductItem` to a row already carrying a `supplierProduct` is
 * caught too.
 */
export function findSupplierProductAndItemConflict({
  submittedRows,
  listData,
}: {
  submittedRows: Partial<IEntityDataItem>[]
  /** The stored rows, for an UPDATE. Omit for a CREATE. */
  listData?: IEntityDataItem[]
}): { index: number; id: TEntityId | undefined } | undefined {
  for (const [index, row] of submittedRows.entries()) {
    const stored = listData?.find((candidate) => candidate.id === row.id)
    const has = (key: string) =>
      Object.prototype.hasOwnProperty.call(row, key)
        ? _isSet((row as Record<string, unknown>)[key])
        : _isSet(stored?.[key])
    if (has('supplierProduct') && has('supplierProductItem')) {
      return { index, id: row.id }
    }
  }
  return undefined
}

/** The 409 body: the server's hint, plus the offending row. */
export function supplierProductAndItemConflictBody({
  id,
}: {
  id: TEntityId | undefined
}) {
  return {
    statusCode: 409,
    message: 'Supplier product and supplier product item cannot both be set.',
    id,
  }
}

/**
 * Wrap a POST or PATCH msService handler so a body that breaks the rule is
 * refused before anything is written. `getEntityListDataFn` is passed for a
 * PATCH, whose rows merge with what is stored.
 */
export function withSupplierProductAndItemGuard(
  handler: (
    req: RestRequest,
    res: ResponseFunction,
    ctx: RestContext
  ) => unknown,
  getEntityListDataFn?: () => Promise<IEntityDataItem[]> | IEntityDataItem[]
) {
  return async (req: RestRequest, res: ResponseFunction, ctx: RestContext) => {
    // JSON for a POST, `application/json-patch+json` (carrying a merge patch)
    // for a PATCH; only a multipart document upload is something else.
    const contentType = req.headers.get('content-type') || ''
    if (!contentType.includes('multipart/form-data')) {
      const body = await req.json()
      const rows = Array.isArray(body) ? body : [body]
      const conflict = findSupplierProductAndItemConflict({
        submittedRows: rows,
        listData: getEntityListDataFn ? await getEntityListDataFn() : undefined,
      })
      if (conflict) {
        return res(
          ctx.status(409),
          ctx.json(supplierProductAndItemConflictBody(conflict))
        )
      }
    }
    return handler(req, res, ctx)
  }
}
